import { MapViewState, ViewStateChangeParameters } from '@deck.gl/core';
import { useRef, useState } from 'react';
import DeckGL, { DeckGLRef } from '@deck.gl/react';
import { APIProvider, limitTiltRange, Map } from '@vis.gl/react-google-maps';

import { MapType } from '../types/enums';
import geoJsonLayer from './streetview/geoJsonLayer';
import scatterplotLayer from './streetview/scatterplotLayer';
import textLayer from './streetview/layerText';
import { FrontendGeoJsonFeature, HouseholdGroup, MapClient } from '../types/global';
import mapStyles from './streetview/mapStyles';
import { MarkerTooltip, selectionRingLayer, useMarkerTooltip } from '../features/streetview';

interface BaseMapProps {
  id: string;
  mapConfig: MapClient;
  mapType: MapType
  areaData: FrontendGeoJsonFeature[] | null;
  householdGroups: HouseholdGroup[] | null;
  mapZoom?: number;
  noCardZoneHeight?: number;
}
export default function BaseMap({
  id, mapConfig, mapType, areaData, householdGroups, mapZoom, noCardZoneHeight,
}: Readonly<BaseMapProps>) {
  const initialViewState: MapViewState = {
    longitude: mapConfig.longitude,
    latitude: mapConfig.latitude,
    zoom: mapZoom ?? 11,
  };
  const [viewState, setViewState] = useState(initialViewState);
  const deckRef = useRef<DeckGLRef | null>(null);

  const {
    clickedMarker,
    setClickedMarker,
    selectedHouseholdCoordinates,
    markerRef,
    mapAreaRef,
    handleMarkerClick,
    handleMapClick,
  } = useMarkerTooltip();
  const updateTooltipViewState = ({ viewState: params }: ViewStateChangeParameters) => {
    const { viewState: limitedViewState } = limitTiltRange({ viewState: params as MapViewState }) as { viewState: MapViewState };

    setViewState(limitedViewState);
    if (clickedMarker?.object && deckRef.current && clickedMarker.coordinate) {
      const viewPort = deckRef.current.deck?.getViewports()[0];
      if (!viewPort) return;
      const [lng, lat] = clickedMarker.coordinate;
      const [x, y] = viewPort.project([lng, lat]);

      setClickedMarker({
        ...clickedMarker,
        x,
        y,
      });
    }
  };
  const areaLayer = areaData ? geoJsonLayer(areaData, handleMarkerClick) : null;
  const householdCountLayer = areaData ? textLayer(areaData) : null;
  const householdLayer = householdGroups ? scatterplotLayer(householdGroups, handleMarkerClick, viewState) : null;
  const ringLayer = householdGroups ? selectionRingLayer(householdGroups, selectedHouseholdCoordinates, viewState) : null;
  const layer = mapType === MapType.HouseholdMap ? [householdLayer, ringLayer] : [areaLayer, householdCountLayer];
  return (
    <APIProvider apiKey={mapConfig.key}>
      <section ref={mapAreaRef} tabIndex={-1} aria-label="Map">
        <DeckGL
          ref={deckRef}
          viewState={viewState}
          onViewStateChange={(params: ViewStateChangeParameters) => updateTooltipViewState(params)}
          onClick={handleMapClick}
          initialViewState={initialViewState}
          style={{ width: '100%', height: '100%' }}
          controller
          layers={layer}
          getCursor={({ isDragging, isHovering }) => {
            if (isHovering) return 'pointer';
            return isDragging ? 'grabbing' : 'grab';
          }}
        >
          <Map
            id={id}
            style={{ width: '100%', height: '100%' }}
            defaultCenter={{ lat: mapConfig.latitude, lng: mapConfig.longitude }}
            defaultZoom={5.9}
            disableDefaultUI
            styles={mapStyles}
          />
          {clickedMarker && (
            <MarkerTooltip
              clickedMarker={clickedMarker}
              setClickedMarker={setClickedMarker}
              noCardZoneHeight={noCardZoneHeight}
              ref={markerRef}
            />
          )}
        </DeckGL>
      </section>
    </APIProvider>
  );
}
